import moment from 'moment'

/**
 * Converts a plain JavaScript object to FormData based on specific rules:
 * - String values: append as-is
 * - Objects with 'text' and 'value' properties: append the 'value'
 * - Arrays of objects with 'text' and 'value' properties: append as [value1, value2, ...]
 * - File objects: append the file
 *
 * @param {Object} obj - The plain object to convert
 * @returns {FormData} - The converted FormData object
 */
export function objectToFormData(obj) {
  const formData = new FormData();

  // Helper function to check if a string looks like a date
  const looksLikeDate = (str) => {
    // Check if string matches DD-MM-YYYY pattern exactly
    return /^\d{2}-\d{2}-\d{4}$/.test(str);
  };

  for (const [key, value] of Object.entries(obj)) {
    // Skip null or undefined values
    if (value === null || value === undefined) {
      continue;
    }

    // Rule 1: If value is a string, append it directly
    if (typeof value === 'string') {
      // Only try to parse as date if it looks like a date format
      if (looksLikeDate(value)) {
        const date = moment(value, 'DD-MM-YYYY', true); // strict parsing
        if (date.isValid()) {
          formData.append(key, date.format('YYYY-MM-DD'));
        } else {
          formData.append(key, value);
        }
      } else {
        formData.append(key, value);
      }

    }
    // Rule 2: If value is a File object, append the file
    else if (value instanceof File) {
      formData.append(key, value);
    }
    // Rule 3: If value is an array of objects with 'text' and 'value' properties
    else if (Array.isArray(value)) {
      // Handle array of File objects (like photos)
      if (value.every(item => item instanceof File)) {
        value.forEach((file, index) => {
          formData.append(`${key}[${index}]`, file);
        });

        continue
      }

      // Check if all items in array have 'text' and 'value' properties
      const isTextValueArray = value.every(item =>
        typeof item === 'object' &&
        item !== null &&
        'text' in item &&
        'value' in item
      );

      if (isTextValueArray && value.length > 0) {
        // Extract values and format as [value1, value2, ...]
        const values = value.map(item => item.value);
        const formattedValue = `[${values.join(', ')}]`;
        formData.append(key, formattedValue);
      } else {
        value.forEach((file, index) => {
          formData.append(`${key}[${index}]`, file);
        });
        console.warn(`Unsupported array format for key "${key}"`);
      }
    }
    // Rule 4: If value is an object with 'text' and 'value' properties, append the 'value'
    else if (
      typeof value === 'object' &&
      value !== null &&
      'text' in value &&
      'value' in value
    ) {
      formData.append(key, value.value);
    }
    // Handle other primitive types (numbers, booleans) by converting to string
    else if (typeof value === 'number' || typeof value === 'boolean') {
      formData.append(key, String(value));
    }
    // For other cases, you might want to handle them or skip them
    else {
      console.warn(`Unsupported value type for key "${key}":`, typeof value);
    }
  }

  return formData;
}
